import * as React from 'react';
const h = React.createElement;

export type BadgeStatus = 'success' | 'error' | 'default' | 'warning' | 'processing';

export interface BadgeProps {
  count?: number;
  size?: 'small' | 'default';
  offset?: [number, number];
  children?: React.ReactNode;
  status?: BadgeStatus;
  text?: React.ReactNode;
  className?: string;
}

const Badge: React.FC<BadgeProps> = function Badge(props) {
  const { count, size = 'default', offset, children, status, text, className = '' } = props;
  const sizeClass =
    size === 'small' ? 'text-[10px] min-w-[16px] h-4 px-1' : 'text-xs min-w-[20px] h-5 px-1.5';

  const statusColorMap: Record<BadgeStatus, string> = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    default: 'bg-dark-500',
    warning: 'bg-yellow-500',
    processing: 'bg-primary-500',
  };

  // Status-only badge (no count, no children)
  if (status && !children) {
    return h(
      'span',
      { className: `inline-flex items-center gap-2 ${className}` },
      h('span', {
        className: `w-2 h-2 rounded-full ${statusColorMap[status] ?? statusColorMap.default} ${status === 'processing' ? 'animate-pulse' : ''}`,
      }),
      text && h('span', { className: 'text-sm text-dark-300' }, text),
    );
  }

  if (!children) {
    return h(
      'span',
      {
        className: `inline-flex items-center justify-center rounded-full bg-red-500 text-white font-medium ${sizeClass} ${className}`,
      },
      count,
    );
  }

  return h(
    'div',
    { className: 'relative inline-flex' },
    children,
    count !== undefined &&
      count !== 0 &&
      h(
        'span',
        {
          className: `absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white font-medium ${sizeClass} ring-2 ring-dark-800`,
          style: offset ? { top: offset[1], right: -offset[0] } : undefined,
        },
        count > 99 ? '99+' : count,
      ),
  );
};

export { Badge };
